import React, { useState, useRef } from "react";
import { s } from "../lib/theme";
import { Attachments, errMsg } from "../lib/api";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.heic,.heif,.webp,.gif,.ics,.txt,image/*,application/pdf,text/calendar";

// Human-readable file size
export function fmtSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Renders the attachment list for a request. If `canManage` is true, shows the
// upload picker + remove buttons. `attachments` comes from the parent (embedded
// in the request); after upload/remove we call `onChanged` to refresh.
export default function AttachmentList({ requestId, attachments = [], canManage = false, canRemove = false, onChanged, label }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  async function pickAndUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr("");
    setBusy(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          throw new Error(`${f.name} is larger than 10 MB`);
        }
        await Attachments.upload(requestId, f);
      }
      if (onChanged) await onChanged();
    } catch (ex) {
      setErr(errMsg(ex, "Upload failed."));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function openFile(a) {
    try {
      await Attachments.openInNewTab(a.id, a.filename);
    } catch (ex) {
      setErr(errMsg(ex, "Could not open file."));
    }
  }

  async function removeFile(a) {
    if (!window.confirm(`Remove "${a.filename}"?`)) return;
    setErr("");
    try {
      await Attachments.remove(a.id);
      if (onChanged) await onChanged();
    } catch (ex) {
      setErr(errMsg(ex, "Could not remove file."));
    }
  }

  const hasAny = attachments && attachments.length > 0;
  if (!hasAny && !canManage) return null;

  return (
    <div style={{ marginTop: 8 }} data-testid={`att-list-${requestId}`}>
      {(hasAny || label) && (
        <div style={{ fontSize: 12, fontWeight: 700, color: "#14304a", textTransform: "uppercase", letterSpacing: ".4px", marginBottom: 6 }}>
          {label || "Attachments"} {hasAny && `(${attachments.length})`}
        </div>
      )}
      {attachments.map((a) => (
        <div
          key={a.id}
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
            border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 4,
            background: "#fafbfa", fontSize: 13,
          }}
          data-testid={`att-${a.id}`}
        >
          <span style={{ fontSize: 15 }}>📎</span>
          <button
            data-testid={`att-open-${a.id}`}
            onClick={() => openFile(a)}
            style={{ background: "none", border: "none", padding: 0, color: "#0f5132", textDecoration: "underline", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
          >
            {a.filename}
          </button>
          <span style={{ color: "#9ca3af", fontSize: 12 }}>· {fmtSize(a.size)}</span>
          <span style={{ color: "#9ca3af", fontSize: 12, marginLeft: "auto" }}>{a.uploaded_by_name}</span>
          {canRemove && (
            <button
              data-testid={`att-remove-${a.id}`}
              onClick={() => removeFile(a)}
              style={{ ...s.btnGhost, padding: "3px 8px", fontSize: 11, color: "#b91c1c", borderColor: "#fecaca" }}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      {canManage && (
        <div style={{ marginTop: 6 }}>
          <input
            ref={inputRef}
            data-testid={`att-upload-${requestId}`}
            type="file"
            accept={ACCEPT}
            multiple
            onChange={pickAndUpload}
            disabled={busy}
            style={{ fontSize: 12 }}
          />
          <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>
            PDF, photo, ICS, up to 10 MB each (max 8 files)
          </span>
          {busy && <span style={{ fontSize: 12, color: "#0f5132", marginLeft: 8 }}>Uploading…</span>}
        </div>
      )}
      {err && <div style={{ ...s.error, fontSize: 12 }}>{err}</div>}
    </div>
  );
}
